import { validateEnumTranslations } from '../../safety_checks';

/**
 * Enums du pipeline de contenu SEO (plan `docs/to_implement/seo/seo-content-pipeline-plan.md`
 * §4.2). Miroir des enums Prisma `seo_*` : toute valeur ajoutée ici doit être migrée en base.
 */

/** Cycle de vie d'un mot-clé (table `seo_keyword`). */
export enum SeoKeywordStatus {
  PROPOSED = 'PROPOSED',
  APPROVED = 'APPROVED',
  BRIEFED = 'BRIEFED',
  WRITTEN = 'WRITTEN',
  PUBLISHED = 'PUBLISHED',
  REJECTED = 'REJECTED',
  /** Une page existante couvre déjà l'intention : rien à écrire. */
  COVERED = 'COVERED',
}

/**
 * Phase du pack d'un pro (`SeoPackSummary.phase`), par priorité d'action. Pas un enum Prisma :
 * calculée à chaque lecture depuis les sujets, les rédactions en file et les articles à relire.
 * Les libellés (avec mois et compteurs) vivent dans `PROFESSIONAL_APP_SEO_ARTICLES_PAGE.PHASE_*`.
 */
export enum SeoPackPhase {
  /**
   * Profil trop vide pour que le moteur trouve des sujets et des concurrents (`profile_gaps`) :
   * l'activation est bloquée tant qu'il n'est pas complété.
   */
  PROFILE_INCOMPLETE = 'PROFILE_INCOMPLETE',
  /** Parcours de première activation pas encore suivi : rien ne tourne. */
  NOT_ONBOARDED = 'NOT_ONBOARDED',
  /** Des articles rédigés attendent la relecture du pro. */
  TO_REVIEW = 'TO_REVIEW',
  /** Rédactions en file ou en cours (`done` / `total` articles du lot). */
  WRITING = 'WRITING',
  /** Première recherche de sujets du mois en cours d'analyse. */
  DISCOVERING = 'DISCOVERING',
  /** Recherche de complément après des « non », pendant le tri. */
  REFILLING = 'REFILLING',
  /** Sujets proposés, le pro trie (`done` « oui » / `total` dates à pourvoir). */
  CHOOSING = 'CHOOSING',
  /** Le tri est complet : il reste à lancer la rédaction. */
  READY_TO_LAUNCH = 'READY_TO_LAUNCH',
  /** Rien à faire : les sujets du mois suivant seront proposés le 10. */
  WAITING_PROPOSALS = 'WAITING_PROPOSALS',
  /**
   * La dernière recherche de sujets du mois suivant n'a rien proposé (0 sujet retenu, ou job en
   * échec) : l'équipe est alertée sur kChat, le pro le lit au lieu d'un « proposés le 10 » trompeur.
   */
  DISCOVERY_EMPTY = 'DISCOVERY_EMPTY',
  /** Toutes les dates du mois suivant sont pourvues (articles programmés ou publiés). */
  ALL_SET = 'ALL_SET',
}

/**
 * Ce qui manque au profil d'un pro pour activer le pack (MEP du 20/10 : un compte sans activité,
 * aux prestations « Prise de contact » / « Hébergement », proposait Carrefour comme concurrent et
 * « prise de contact vence » comme sujet). Le moteur cherche avec le métier, les prestations et
 * la ville : sans eux, il n'a rien de juste à chercher.
 */
export enum SeoProfileGap {
  /** Aucune activité exploitable (aucune, ou seulement « Autre » sans libellé). */
  ACTIVITY = 'ACTIVITY',
  /** Moins de `SEO_PROFILE_MIN_PRESTATIONS` prestations qui nomment un service. */
  PRESTATIONS = 'PRESTATIONS',
  /** Référencement local sans aucune ville (ni zone déclarée, ni adresse d'établissement). */
  AREA = 'AREA',
}

/** Prestations nommant un vrai service (hors « Prise de contact », « Devis gratuit »…) exigées. */
export const SEO_PROFILE_MIN_PRESTATIONS = 2;

/** D'où vient un mot-clé candidat (plusieurs sources possibles). */
export enum SeoKeywordSource {
  GSC = 'GSC',
  AUTOCOMPLETE = 'AUTOCOMPLETE',
  SERP = 'SERP',
  COMPETITOR = 'COMPETITOR',
  HALOSCAN = 'HALOSCAN',
  LLM = 'LLM',
  MANUAL = 'MANUAL',
}

export enum SeoBriefStatus {
  DRAFT = 'DRAFT',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

export enum SeoArticleStatus {
  GENERATING = 'GENERATING',
  QA_FAILED = 'QA_FAILED',
  READY = 'READY',
  PUBLISHED = 'PUBLISHED',
  REJECTED = 'REJECTED',
}

/**
 * Index d'où provient un lien entrant constaté (table `seo_external_backlink`). Miroir de l'enum
 * Prisma `seo_backlink_provider`. Jamais nommé au pro (plan Bing, D1 : « d'après les index des
 * moteurs ») : sert aux traitements et à l'admin.
 */
export enum SeoBacklinkProvider {
  BING = 'BING',
}

/**
 * Offre au titre de laquelle un lien (`seo_backlink`) a été commandé. Miroir de l'enum Prisma
 * `seo_backlink_origin` ; null pour les anciennes lignes du blog Propelsia (dispositif abandonné).
 * Plan : docs/to_implement/seo/seo-upsell-offers.md (U1, U4) et linkuma-integration.md §7.
 */
export enum SeoBacklinkOrigin {
  PACK = 'PACK',
  ADDON = 'ADDON',
  ACCELERATOR = 'ACCELERATOR',
  /** Commandé à la main par l'équipe chez le fournisseur (historique importé) : hors quotas des offres. */
  TEAM = 'TEAM',
}

/** Origines comptées dans les quotas mensuels des offres (tout sauf les commandes de l'équipe). */
export const SEO_OFFER_ORIGINS: readonly SeoBacklinkOrigin[] = [
  SeoBacklinkOrigin.PACK,
  SeoBacklinkOrigin.ADDON,
  SeoBacklinkOrigin.ACCELERATOR,
];

/**
 * Cycle de vie d'un accélérateur 3 mois (`seo_backlink_accelerator`). Miroir de l'enum Prisma
 * `seo_backlink_accelerator_status`. PENDING : payé, démarre au 1er du mois suivant ; ACTIVE : au
 * moins un mois commandé ; COMPLETED : tous les liens commandés ; CANCELLED : plan Max perdu
 * pendant la fenêtre (liens restants perdus, pas de remboursement — U11).
 */
export enum SeoBacklinkAcceleratorStatus {
  PENDING = 'PENDING',
  ACTIVE = 'ACTIVE',
  COMPLETED = 'COMPLETED',
  CANCELLED = 'CANCELLED',
}

/**
 * Cycle de vie d'un lien commandé au fournisseur (`seo_backlink.status`, linkuma-integration.md §6,
 * §7). Miroir de l'enum Prisma `seo_backlink_status`. Les anciennes lignes du blog Propelsia sont
 * PUBLISHED (valeur par défaut de la colonne).
 *  - ORDERED : ligne créée, commande passée (ou à passer : `provider_order_id` nul = à renvoyer) ;
 *  - IN_PROGRESS : date de démarrage atteinte, l'article est en rédaction chez le partenaire ;
 *  - PUBLISHED : article en ligne (`source_url`), vérification du lien à suivre ;
 *  - PUBLISHED_MISMATCH : en ligne, mais le lien vérifié ne correspond pas (ancre, nofollow, cible) ;
 *  - REPLACED : lien mort remplacé par le support du fournisseur ;
 *  - FAILED : commande refusée (422 solde, thématique…) — relance manuelle.
 */
export enum SeoBacklinkStatus {
  /** Mode B : article rédigé par le moteur, en attente de validation du pro (7 jours). */
  TO_REVIEW = 'TO_REVIEW',
  /** Mode B : pas de validation à J+7, mois perdu (pas de commande). */
  EXPIRED = 'EXPIRED',
  ORDERED = 'ORDERED',
  IN_PROGRESS = 'IN_PROGRESS',
  PUBLISHED = 'PUBLISHED',
  PUBLISHED_MISMATCH = 'PUBLISHED_MISMATCH',
  REPLACED = 'REPLACED',
  FAILED = 'FAILED',
}

/** Famille de la page cible d'un lien commandé (rotation, linkuma-integration.md §2). Miroir Prisma `seo_backlink_target_family`. */
export enum SeoBacklinkTargetFamily {
  HOME = 'HOME',
  SERVICE = 'SERVICE',
  NEWS = 'NEWS',
}

export enum SeoLinkDirection {
  /** Lien posé dans l'article vers une page existante. */
  OUT = 'OUT',
  /** Lien ajouté dans une page existante vers l'article (maillage rétroactif). */
  BACK = 'BACK',
}

/**
 * Rédaction des articles publiés sur les sites partenaires (backlinks) : au choix du pro, stocké
 * dans ses réglages SEO (`SeoSettingsSchema.backlink_mode`, JSON, pas d'enum Prisma). AUTO : l'article
 * part sans relecture, figé une fois publié (le pro l'a accepté en choisissant ce mode). REVIEW : le
 * pro valide chaque article avant envoi, au prix d'un délai et de son temps.
 */
export enum SeoBacklinkMode {
  AUTO = 'AUTO',
  REVIEW = 'REVIEW',
}

/** Nature d'un problème relevé par la QA (score déterministe ou juge vérifié). */
export enum SeoQaIssueKind {
  FORBIDDEN_TERM = 'FORBIDDEN_TERM',
  UNSOURCED_NUMBER = 'UNSOURCED_NUMBER',
  VOICE = 'VOICE',
  INVENTED_LINK = 'INVENTED_LINK',
  MISSING_LINK = 'MISSING_LINK',
  MISSING_SECTION = 'MISSING_SECTION',
  UNANSWERED_QUESTION = 'UNANSWERED_QUESTION',
  RISKY_CLAIM = 'RISKY_CLAIM',
  COMPETITOR = 'COMPETITOR',
  STYLE = 'STYLE',
  STRUCTURE = 'STRUCTURE',
  LENGTH = 'LENGTH',
  REASONING_LEAK = 'REASONING_LEAK',
  /** Article local sans ancrage réel : la ville est plaquée ou absente (règle `LOCAL_ANGLE`, juge `generic_local`). */
  LOCAL_ANGLE = 'LOCAL_ANGLE',
  OTHER = 'OTHER',
}

/**
 * Pourquoi le moteur a écarté un sujet AVANT de l'écrire (porte « écrivable » du brief, 20/10) :
 * un article écrit sur un sujet qui ne se tient pas (SERP d'un autre métier, plan qui exige des
 * témoignages qu'on n'a pas, prix sans tarif au catalogue) finit en inventions. Miroir de l'enum
 * Prisma `seo_unwritable_reason` (`seo_keyword.unwritable_reason`) ; le détail en clair va dans
 * `rejected_reason`, relu par le classifieur comme un refus du pro.
 */
export enum SeoUnwritableReason {
  /** Les pages qui se positionnent vendent un autre service que celui du pro (maintenance informatique pour une agence web). */
  OFF_TRADE = 'OFF_TRADE',
  /** Classement ou annuaire de prestataires (« top 5 des coiffeurs à Nice ») : le pro ferait la publicité de ses concurrents. */
  LISTICLE = 'LISTICLE',
  /** Une fois retirées les sections témoignages / études de cas / réalisations, le plan ne tient plus. */
  UNSOURCED_OUTLINE = 'UNSOURCED_OUTLINE',
  /** Sujet de prix sans aucune prestation du catalogue qui y réponde. */
  MISSING_FACT = 'MISSING_FACT',
  /** Même page qu'un sujet déjà écrit ou en cours, formulé autrement (prix / tarif, site web / site internet). */
  VARIANT_OF_WRITTEN = 'VARIANT_OF_WRITTEN',
}

export const SEO_KEYWORD_STATUS_TRANSLATE_KEY = 'SEO_KEYWORD_STATUS_';
export const SEO_KEYWORD_SOURCE_TRANSLATE_KEY = 'SEO_KEYWORD_SOURCE_';
export const SEO_BRIEF_STATUS_TRANSLATE_KEY = 'SEO_BRIEF_STATUS_';
export const SEO_ARTICLE_STATUS_TRANSLATE_KEY = 'SEO_ARTICLE_STATUS_';
export const SEO_LINK_DIRECTION_TRANSLATE_KEY = 'SEO_LINK_DIRECTION_';
export const SEO_QA_ISSUE_KIND_TRANSLATE_KEY = 'SEO_QA_ISSUE_KIND_';
export const SEO_BACKLINK_MODE_TRANSLATE_KEY = 'SEO_BACKLINK_MODE_';
export const SEO_BACKLINK_ORIGIN_TRANSLATE_KEY = 'SEO_BACKLINK_ORIGIN_';
export const SEO_ACCELERATOR_STATUS_TRANSLATE_KEY = 'SEO_ACCELERATOR_STATUS_';
export const SEO_PROFILE_GAP_TRANSLATE_KEY = 'SEO_PROFILE_GAP_';
export const SEO_UNWRITABLE_REASON_TRANSLATE_KEY = 'SEO_UNWRITABLE_REASON_';

export const SeoKeywordStatusTranslation = {
  'fr-FR': {
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.PROPOSED}`]: 'Proposé',
    // Sujet lancé dont le brief n'a pas encore commencé (quelques minutes en file) : « Validé »
    // seul laissait croire que rien ne se passait après le lancement.
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.APPROVED}`]: 'Rédaction en préparation',
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.BRIEFED}`]: 'En rédaction',
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.WRITTEN}`]: 'Article rédigé',
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.PUBLISHED}`]: 'Publié',
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.REJECTED}`]: 'Refusé',
    [`${SEO_KEYWORD_STATUS_TRANSLATE_KEY}${SeoKeywordStatus.COVERED}`]: 'Déjà couvert',
  },
};

export const SeoKeywordSourceTranslation = {
  'fr-FR': {
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.GSC}`]: 'Search Console',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.AUTOCOMPLETE}`]: 'Suggestions Google',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.SERP}`]: 'Résultats de recherche',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.COMPETITOR}`]: 'Concurrents',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.HALOSCAN}`]: 'Haloscan',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.LLM}`]: 'Suggestion IA',
    [`${SEO_KEYWORD_SOURCE_TRANSLATE_KEY}${SeoKeywordSource.MANUAL}`]: 'Saisie manuelle',
  },
};

export const SeoBriefStatusTranslation = {
  'fr-FR': {
    [`${SEO_BRIEF_STATUS_TRANSLATE_KEY}${SeoBriefStatus.DRAFT}`]: 'Brouillon',
    [`${SEO_BRIEF_STATUS_TRANSLATE_KEY}${SeoBriefStatus.APPROVED}`]: 'Validé',
    [`${SEO_BRIEF_STATUS_TRANSLATE_KEY}${SeoBriefStatus.REJECTED}`]: 'Refusé',
  },
};

export const SeoArticleStatusTranslation = {
  'fr-FR': {
    [`${SEO_ARTICLE_STATUS_TRANSLATE_KEY}${SeoArticleStatus.GENERATING}`]: 'En cours de rédaction',
    [`${SEO_ARTICLE_STATUS_TRANSLATE_KEY}${SeoArticleStatus.QA_FAILED}`]: 'Contrôle qualité échoué',
    [`${SEO_ARTICLE_STATUS_TRANSLATE_KEY}${SeoArticleStatus.READY}`]: 'Prêt à relire',
    [`${SEO_ARTICLE_STATUS_TRANSLATE_KEY}${SeoArticleStatus.PUBLISHED}`]: 'Publié',
    [`${SEO_ARTICLE_STATUS_TRANSLATE_KEY}${SeoArticleStatus.REJECTED}`]: 'Refusé',
  },
};

export const SeoLinkDirectionTranslation = {
  'fr-FR': {
    [`${SEO_LINK_DIRECTION_TRANSLATE_KEY}${SeoLinkDirection.OUT}`]: 'Lien sortant',
    [`${SEO_LINK_DIRECTION_TRANSLATE_KEY}${SeoLinkDirection.BACK}`]: 'Lien entrant',
  },
};

export const SeoBacklinkModeTranslation = {
  'fr-FR': {
    [`${SEO_BACKLINK_MODE_TRANSLATE_KEY}${SeoBacklinkMode.AUTO}`]: 'Tout en automatique',
    [`${SEO_BACKLINK_MODE_TRANSLATE_KEY}${SeoBacklinkMode.REVIEW}`]: 'Je relis avant publication',
  },
};

/** Mention « Offre : … » sous chaque lien de la liste (jamais le nom du fournisseur, D6). */
export const SeoBacklinkOriginTranslation = {
  'fr-FR': {
    [`${SEO_BACKLINK_ORIGIN_TRANSLATE_KEY}${SeoBacklinkOrigin.PACK}`]: 'Offre : pack référencement',
    [`${SEO_BACKLINK_ORIGIN_TRANSLATE_KEY}${SeoBacklinkOrigin.ADDON}`]: 'Offre : liens supplémentaires',
    [`${SEO_BACKLINK_ORIGIN_TRANSLATE_KEY}${SeoBacklinkOrigin.ACCELERATOR}`]: 'Offre : accélérateur',
    [`${SEO_BACKLINK_ORIGIN_TRANSLATE_KEY}${SeoBacklinkOrigin.TEAM}`]: 'Commandé par l’équipe',
  },
};

export const SeoBacklinkAcceleratorStatusTranslation = {
  'fr-FR': {
    [`${SEO_ACCELERATOR_STATUS_TRANSLATE_KEY}${SeoBacklinkAcceleratorStatus.PENDING}`]: 'À venir',
    [`${SEO_ACCELERATOR_STATUS_TRANSLATE_KEY}${SeoBacklinkAcceleratorStatus.ACTIVE}`]: 'En cours',
    [`${SEO_ACCELERATOR_STATUS_TRANSLATE_KEY}${SeoBacklinkAcceleratorStatus.COMPLETED}`]: 'Terminé',
    [`${SEO_ACCELERATOR_STATUS_TRANSLATE_KEY}${SeoBacklinkAcceleratorStatus.CANCELLED}`]: 'Interrompu',
  },
};

/** Ce qu'il reste à faire, écrit comme une action (écran « complétez votre profil »). */
export const SeoProfileGapTranslation = {
  'fr-FR': {
    [`${SEO_PROFILE_GAP_TRANSLATE_KEY}${SeoProfileGap.ACTIVITY}`]: 'Choisissez votre activité',
    [`${SEO_PROFILE_GAP_TRANSLATE_KEY}${SeoProfileGap.PRESTATIONS}`]: `Ajoutez au moins ${SEO_PROFILE_MIN_PRESTATIONS} prestations à votre catalogue`,
    [`${SEO_PROFILE_GAP_TRANSLATE_KEY}${SeoProfileGap.AREA}`]: 'Indiquez votre zone d’intervention',
  },
};

/** Ce que le pro lit sous un sujet écarté par le moteur (liste des sujets) : le pourquoi, et ce qui le débloquerait quand c'est lui. */
export const SeoUnwritableReasonTranslation = {
  'fr-FR': {
    [`${SEO_UNWRITABLE_REASON_TRANSLATE_KEY}${SeoUnwritableReason.OFF_TRADE}`]:
      'Les pages qui se positionnent sur cette recherche parlent d’un autre métier que le vôtre : l’article n’aurait pas sa place.',
    [`${SEO_UNWRITABLE_REASON_TRANSLATE_KEY}${SeoUnwritableReason.LISTICLE}`]:
      'Cette recherche appelle un classement de prestataires : écrire dessus reviendrait à présenter vos concurrents.',
    [`${SEO_UNWRITABLE_REASON_TRANSLATE_KEY}${SeoUnwritableReason.UNSOURCED_OUTLINE}`]:
      'Ce sujet se traite avec des témoignages et des études de cas que nous n’avons pas : l’article aurait dû les inventer.',
    [`${SEO_UNWRITABLE_REASON_TRANSLATE_KEY}${SeoUnwritableReason.MISSING_FACT}`]:
      'Ce sujet parle de prix, et aucune prestation de votre catalogue n’y répond : ajoutez-la avec son tarif pour le débloquer.',
    [`${SEO_UNWRITABLE_REASON_TRANSLATE_KEY}${SeoUnwritableReason.VARIANT_OF_WRITTEN}`]:
      'Un article déjà écrit ou en cours traite le même sujet sous une autre formulation : deux pages se feraient concurrence.',
  },
};

export const SEO_BACKLINK_STATUS_TRANSLATE_KEY = 'SEO_BACKLINK_STATUS_';
export const SEO_BACKLINK_TARGET_FAMILY_TRANSLATE_KEY = 'SEO_BACKLINK_TARGET_FAMILY_';

/** Libellés vus par le pro : un écart de vérification reste « en vérification », jamais une alarme. */
export const SeoBacklinkStatusTranslation = {
  'fr-FR': {
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.TO_REVIEW}`]: 'À relire',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.EXPIRED}`]: 'Non validé à temps',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.ORDERED}`]: 'Commandé',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.IN_PROGRESS}`]: 'En rédaction',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.PUBLISHED}`]: 'Publié',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.PUBLISHED_MISMATCH}`]: 'En vérification',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.REPLACED}`]: 'Remplacé',
    [`${SEO_BACKLINK_STATUS_TRANSLATE_KEY}${SeoBacklinkStatus.FAILED}`]: 'Reporté',
  },
};

export const SeoBacklinkTargetFamilyTranslation = {
  'fr-FR': {
    [`${SEO_BACKLINK_TARGET_FAMILY_TRANSLATE_KEY}${SeoBacklinkTargetFamily.HOME}`]: 'Page d’accueil',
    [`${SEO_BACKLINK_TARGET_FAMILY_TRANSLATE_KEY}${SeoBacklinkTargetFamily.SERVICE}`]: 'Fiche prestation',
    [`${SEO_BACKLINK_TARGET_FAMILY_TRANSLATE_KEY}${SeoBacklinkTargetFamily.NEWS}`]: 'Actualité',
  },
};

export const SeoQaIssueKindTranslation = {
  'fr-FR': {
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.FORBIDDEN_TERM}`]: 'Terme interdit',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.UNSOURCED_NUMBER}`]: 'Chiffre non sourcé',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.VOICE}`]: 'Voix non respectée',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.INVENTED_LINK}`]: 'Lien inventé',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.MISSING_LINK}`]: 'Lien du brief manquant',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.MISSING_SECTION}`]: 'Section du plan manquante',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.UNANSWERED_QUESTION}`]: 'Question sans réponse',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.RISKY_CLAIM}`]: 'Allégation risquée',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.COMPETITOR}`]: 'Concurrent cité',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.STYLE}`]: 'Style',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.STRUCTURE}`]: 'Structure',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.LENGTH}`]: 'Longueur',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.REASONING_LEAK}`]: 'Raisonnement déversé',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.LOCAL_ANGLE}`]: 'Ancrage local insuffisant',
    [`${SEO_QA_ISSUE_KIND_TRANSLATE_KEY}${SeoQaIssueKind.OTHER}`]: 'Autre',
  },
};

validateEnumTranslations(SeoKeywordStatus, SeoKeywordStatusTranslation, SEO_KEYWORD_STATUS_TRANSLATE_KEY, true);
validateEnumTranslations(SeoKeywordSource, SeoKeywordSourceTranslation, SEO_KEYWORD_SOURCE_TRANSLATE_KEY, true);
validateEnumTranslations(SeoBriefStatus, SeoBriefStatusTranslation, SEO_BRIEF_STATUS_TRANSLATE_KEY, true);
validateEnumTranslations(SeoArticleStatus, SeoArticleStatusTranslation, SEO_ARTICLE_STATUS_TRANSLATE_KEY, true);
validateEnumTranslations(SeoLinkDirection, SeoLinkDirectionTranslation, SEO_LINK_DIRECTION_TRANSLATE_KEY, true);
validateEnumTranslations(SeoQaIssueKind, SeoQaIssueKindTranslation, SEO_QA_ISSUE_KIND_TRANSLATE_KEY, true);
validateEnumTranslations(SeoBacklinkMode, SeoBacklinkModeTranslation, SEO_BACKLINK_MODE_TRANSLATE_KEY, true);
validateEnumTranslations(SeoBacklinkOrigin, SeoBacklinkOriginTranslation, SEO_BACKLINK_ORIGIN_TRANSLATE_KEY, true);
validateEnumTranslations(
  SeoBacklinkAcceleratorStatus,
  SeoBacklinkAcceleratorStatusTranslation,
  SEO_ACCELERATOR_STATUS_TRANSLATE_KEY,
  true,
);
validateEnumTranslations(SeoBacklinkStatus, SeoBacklinkStatusTranslation, SEO_BACKLINK_STATUS_TRANSLATE_KEY, true);
validateEnumTranslations(SeoProfileGap, SeoProfileGapTranslation, SEO_PROFILE_GAP_TRANSLATE_KEY, true);
validateEnumTranslations(
  SeoUnwritableReason,
  SeoUnwritableReasonTranslation,
  SEO_UNWRITABLE_REASON_TRANSLATE_KEY,
  true,
);
validateEnumTranslations(
  SeoBacklinkTargetFamily,
  SeoBacklinkTargetFamilyTranslation,
  SEO_BACKLINK_TARGET_FAMILY_TRANSLATE_KEY,
  true,
);
